<template>
  <div class="racq-dropdown">
    <button
      type="button"
      class="racq-dropdown__button"
      :class="{
        'racq-dropdown__button--sm': isSmall,
        'racq-dropdown__button--disabled': isDisabled,
      }"
      role="combobox"
      aria-haspopup="listbox"
      :aria-expanded="isOpen"
      :aria-controls="dropdownId"
      @click.stop="toggleDropdown"
      @keydown.stop.prevent="onKeyDown"
    >
      <div class="flex justify-between items-center w-full">
        <span :class="{ 'text-m-bold': isTextMBold }">
          {{
            context.attrs.keepPlaceHolder ? context.attrs.placeholder : (selectedOption ?? context.attrs.placeholder)
          }}</span
        >

        <svg
          class="racq-dropdown__icon transition-all"
          :class="{
            'racq-dropdown__icon--disabled': isDisabled,
            'rotate-180': isOpen,
          }"
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="M3 5L8 10L13 5" stroke="currentColor" stroke-width="2" />
        </svg>
      </div>
    </button>

    <!-- Dropdown Options -->
    <div
      v-show="isOpen && !isDisabled"
      class="racq-dropdown__menu"
      :class="{
        'racq-dropdown__menu--sm': isSmall,
        'racq-dropdown__menu--align-right': isMenuAlignRight,
      }"
    >
      <div :id="dropdownId" class="p-1" role="listbox" :aria-activedescendant="`option-${highlightedIndex}`">
        <button
          v-for="(option, index) in context.options"
          :key="index"
          type="button"
          role="option"
          :aria-selected="index === highlightedIndex"
          class="racq-dropdown__option"
          :class="{ 'bg-racq-anti-flash-white': index === highlightedIndex }"
          @click.stop="selectOption(option)"
          @keydown.stop.prevent="onKeyDown"
        >
          {{ option.label }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useUniqueId } from '@/utils/common-helper';
import { ref, onMounted, onBeforeUnmount, computed } from 'vue';

const props = defineProps<{
  context: any;
}>();

const isOpen = ref(false);
const selectedOption = ref(props.context?.value || null);
const highlightedIndex = ref(-1);
const dropdownId = useUniqueId('dropdown-menu');

const hasClassOption = (className: string) => props.context?.attrs?.classesOptions?.includes(className) || false;
const isSmall = computed(() => hasClassOption('dropdown-sm'));
const isDisabled = computed(() => hasClassOption('dropdown-disabled'));
const isTextMBold = computed(() => hasClassOption('text-m-bold'));
const isMenuAlignRight = computed(() => hasClassOption('dropdown-menu-align-right'));

const toggleDropdown = () => {
  isOpen.value = !isOpen.value;
};

const selectOption = (option: any) => {
  selectedOption.value = option.label;
  isOpen.value = false; // Close dropdown
  emitChange(option.value);
};

// Emit change to FormKit
const emitChange = (value: any) => {
  props.context?.node.input(value);
};

// Close dropdown when clicking outside
const closeDropdown = (e: MouseEvent) => {
  if (!(e.target as HTMLElement).closest('.racq-dropdown')) {
    isOpen.value = false;
  }
};

onMounted(() => {
  document.addEventListener('click', closeDropdown);
});

onBeforeUnmount(() => {
  document.removeEventListener('click', closeDropdown);
});

const onKeyDown = (e: KeyboardEvent) => {
  if (isDisabled.value) return;

  const total = props.context.options.length;

  if (!isOpen.value) {
    // Open dropdown on Enter or Space when focused
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault(); // Prevent scrolling for Space
      isOpen.value = true;
      highlightedIndex.value = 0;
    }
    // Also support ArrowDown or ArrowUp for opening
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      isOpen.value = true;
      highlightedIndex.value = 0;
    }
    return;
  }

  // Navigation and selection
  switch (e.key) {
    case 'ArrowDown':
      highlightedIndex.value = (highlightedIndex.value + 1) % total;
      break;
    case 'ArrowUp':
      highlightedIndex.value = (highlightedIndex.value - 1 + total) % total;
      break;
    case 'Enter':
    case ' ':
      if (highlightedIndex.value >= 0) {
        e.preventDefault();
        selectOption(props.context.options[highlightedIndex.value]);
      }
      break;
    case 'Escape':
      isOpen.value = false;
      break;
  }
};
</script>
